import React, { useState } from 'react'
import { TemplateType } from './Template'
import { PiDownloadSimpleLight } from 'react-icons/pi'
import parse from 'html-react-parser'
import domToReact from 'html-react-parser/lib/dom-to-react'

const DocumentDynamicEntry = ({
  title,
  content,
}: {
  title: string
  content: string
}) => (
  <div className='flex items-center gap-1'>
    <span className='font-medium lg:font-bold text-sm lg:text-lg text-[#001A3D] tracking-wide'>
      {title}:
    </span>
    <span className='font-medium text-xs lg:text-base text-[#37455C] tracking-wide'>
      {content}
    </span>
  </div>
)

export const TemplateCardBig = ({
  template,
  onDownloadClick,
  isCheckoutSessionLoading,
  className,
}: {
  template: TemplateType
  onDownloadClick
  isCheckoutSessionLoading
  className?: string
}) => {
  const [expanded, setExpanded] = useState(false)

  const options = {
    replace: (domNode) => {
      if (domNode.type === 'tag') {
        switch (domNode.name) {
          case 'h1':
            return (
              <h1 className='text-base lg:text-4xl font-bold'>
                {domToReact(domNode.children)}
              </h1>
            )
          case 'h2':
            return (
              <h2 className='text-base lg:text-lg font-medium lg:font-bold text-[#37A2BA] tracking-wide pt-3 lg:pt-[26px]'>
                {domToReact(domNode.children)}
              </h2>
            )
          case 'h3':
            return (
              <h3 className='text-base font-medium lg:font-bold text-[#37A2BA] tracking-wide'>
                {domToReact(domNode.children)}
              </h3>
            )
          case 'p':
            return (
              <p className='text-sm lg:text-base text-[#405985] tracking-wide leading-tight pt-2'>
                {domToReact(domNode.children)}
              </p>
            )
          case 'ul':
            return (
              <ul className='list-disc pl-8 text-sm lg:text-base text-[#405985]'>
                {domToReact(domNode.children)}
              </ul>
            )
          case 'ol':
            return (
              <ol className='list-disc pl-8 text-sm lg:text-base text-[#405985]'>
                {domToReact(domNode.children)}
              </ol>
            )
          case 'li':
            return (
              <li className='text-xs lg:text-sm text-[#405985]'>
                {domToReact(domNode.children)}
              </li>
            )
          default:
            return undefined
        }
      }
    },
  }

  return (
    <div
      className={`${className} flex flex-col 
            py-8 px-6 lg:pl-[120px] lg:py-[80px] lg:pr-[80px] 
            max-w-[1100px] w-full
            bg-[linear-gradient(180deg,rgba(128,159,242,0.3)_0%,rgba(255,255,255,0.3)_30%)] 
            shadow-[1px_0px_150px_0px_rgba(0,0,0,0.12)]
            rounded-tr-[40px] rounded-br-[40px]`}
    >
      <div
        className={`flex flex-col gap-8 lg:gap-[100px]
                overflow-hidden transition-all duration-300 
                ${expanded ? 'max-h-none' : 'max-h-[1478px]'} sm:max-h-none`}
      >
        <div className={`flex flex-col gap-[30px] lg:gap-[50px]`}>
          <div className='flex flex-col gap-4 lg:gap-[29px]'>
            <h1 className='text-lg lg:text-[64px] text-[#060666] font-bold leading-none'>
              {template.title}
            </h1>
            <p className='font-medium lg:font-bold text-sm lg:text-lg text-[#122D5C]'>
              {template.description}
            </p>
            <div className='flex flex-col lg:flex-row items-start lg:items-center gap-1 lg:gap-5'>
              <DocumentDynamicEntry
                title='ID'
                content={`#${template.id.slice(0, 8)}`}
              />
              <DocumentDynamicEntry
                title='Type'
                content={template.type?.name}
              />
              <DocumentDynamicEntry title='Version' content='V4.0' />
            </div>
          </div>

          <div className='flex gap-[19px] font-medium text-xs lg:text-base tracking-wide'>
            <button
              // onClick={}
              // disabled={false}
              className='px-[8px] py-[6px] lg:px-[12px] lg:py-[12px] rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] justify-center items-center gap-2.5 inline-flex
                                bg-[#020251] text-white
                                hover:text-[#65C2D7]
                                active:text-white
                                disabled:bg-[#f4f4f4] disabled:text-[#a0abbe]'
            >
              <div className='text-center leading-none tracking-wide'>
                Create
              </div>
            </button>
            <button
              // onClick={}
              // disabled={false}
              className='px-[8px] py-[6px] lg:px-[12px] lg:py-[12px] rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] justify-center items-center gap-2.5 inline-flex
                                bg-white text-[#1c7d92]
                                hover:bg-[#EBFBFF]
                                active:bg-white
                                disabled:text-[#A0ABBE] disabled:bg-[#F4F4F4]'
            >
              <div className='text-center leading-none tracking-wide'>
                Preview
              </div>
            </button>
            <button
              disabled={isCheckoutSessionLoading}
              onClick={onDownloadClick}
              className='flex items-center text-muted gap-[6px]
                                text-[#001A3D]
                                hover:text-[#6596D7] 
                                active:text-[#001A3D]
                                disabled:text-[#A0ABBE]'
            >
              <span>Download</span>
              <PiDownloadSimpleLight
                className='w-full h-full'
                aria-label='Download document'
              />
            </button>
          </div>
        </div>

        <div className={`flex flex-col gap-[24px]`}>
          <h4 className='text-base lg:text-2xl font-medium lg:font-bold text-[#122D5C] tracking-wide'>
            Summary
          </h4>
          <span className=''>{parse(template.summary, options)}</span>
        </div>

        <div className=''>
          <h4 className='text-[#1C7D93] text-base lg:text-2xl font-medium lg:font-bold'>
            Content
          </h4>
          <span className='flex flex-col'>
            {parse(template.content, options)}
          </span>
        </div>
      </div>

      <button
        className='flex fle-grow justify-end text-[#001A3D] text-xs font-medium underline sm:hidden pt-[40px]'
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'Show Less' : 'Show More'}
      </button>
    </div>
  )
}
